import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchPentestTools, fetchPentestEngagements, fetchAllEngagements, fetchScanJobs, fetchPentestReports, launchScan,
  createEngagement, approveEngagement, rejectEngagement, revokeEngagement,
} from '../../services/api/pentestService';

export const loadPentest = createAsyncThunk('pentest/load', async () => {
  const [tools, engagements, allEngagements, jobs, reports] = await Promise.all([
    fetchPentestTools(), fetchPentestEngagements(), fetchAllEngagements(), fetchScanJobs(), fetchPentestReports(),
  ]);
  return { tools, engagements, allEngagements, jobs, reports };
});

export const startScan = createAsyncThunk('pentest/startScan', async ({ engagementId, toolId, target }, { rejectWithValue }) => {
  try { return await launchScan(engagementId, toolId, target); }
  catch (err) {
    const data = err?.response?.data;
    const fields = Array.isArray(data?.fieldErrors) ? data.fieldErrors.join(' ') : '';
    return rejectWithValue(fields || data?.detail || data?.message || 'Could not launch the penetration test.');
  }
});

const refreshEngagements = async () => {
  const [engagements, allEngagements] = await Promise.all([fetchPentestEngagements(), fetchAllEngagements()]);
  return { engagements, allEngagements };
};

export const requestEngagement = createAsyncThunk('pentest/requestEngagement', async (payload, { rejectWithValue }) => {
  try {
    await createEngagement(payload);
    return await refreshEngagements();
  } catch (err) {
    const data = err?.response?.data;
    const LABELS = { name: 'Name', justification: 'Justification', target_scope: 'Target scope', allowed_tools: 'Allowed tools', window_start: 'Window start', window_end: 'Window end' };
    const fields = Array.isArray(data?.fieldErrors)
      ? data.fieldErrors.map((f) => {
        const m = String(f).match(/^(?:body\.)?([a-z_]+)(?:\.\d+)?:\s*(.*)$/i);
        return m ? `${LABELS[m[1]] || m[1]}: ${m[2].replace(/^Value error,\s*/i, '')}.` : String(f);
      }).join(' ')
      : '';
    const detail = typeof data?.detail === 'string' ? data.detail : '';
    return rejectWithValue(fields || detail || data?.message || 'Could not submit this engagement request.');
  }
});

export const approveEngagementRequest = createAsyncThunk('pentest/approveEngagement', async (id) => {
  await approveEngagement(id);
  return refreshEngagements();
});

export const rejectEngagementRequest = createAsyncThunk('pentest/rejectEngagement', async ({ id, reason }) => {
  await rejectEngagement(id, reason);
  return refreshEngagements();
});

export const revokeEngagementRequest = createAsyncThunk('pentest/revokeEngagement', async (id) => {
  await revokeEngagement(id);
  return refreshEngagements();
});

const pentestSlice = createSlice({
  name: 'pentest',
  initialState: { status: 'idle', error: null, tools: [], engagements: [], allEngagements: [], jobs: [], reports: [] },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadPentest.pending, (state) => { state.status = 'loading'; })
      .addCase(loadPentest.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.tools = action.payload.tools;
        state.engagements = action.payload.engagements;
        state.allEngagements = action.payload.allEngagements;
        state.jobs = action.payload.jobs;
        state.reports = action.payload.reports;
      })
      .addCase(loadPentest.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(startScan.fulfilled, (state, action) => {
        state.jobs.unshift(action.payload);
      });

    for (const thunk of [requestEngagement, approveEngagementRequest, rejectEngagementRequest, revokeEngagementRequest]) {
      builder.addCase(thunk.fulfilled, (state, action) => {
        state.engagements = action.payload.engagements;
        state.allEngagements = action.payload.allEngagements;
      });
    }
  }
});

export default pentestSlice.reducer;
